import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { QRCodeSVG } from 'qrcode.react';
import { PencilIcon, TrashIcon, ClipboardIcon } from '@heroicons/react/20/solid';
import {
    Settings, Copy, Clock, CheckCircle, XCircle, ExternalLink, AlertTriangle,
    QrCode, CalendarPlus, Download,
} from 'lucide-react';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Cuánto queda de vida al enlace, en lenguaje llano.
 *
 * Una fecha absoluta obliga a calcular mentalmente si un enlace sigue sirviendo
 * o no, que es justo lo que se viene a mirar aquí.
 */
function expiryNotice(expiresAt) {
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (Number.isNaN(remaining)) return null;
    if (remaining <= 0) return { text: 'Caducado', urgent: true };

    const days = Math.floor(remaining / DAY_MS);
    if (days >= 2) return { text: `Caduca en ${days} días`, urgent: days <= 7 };

    const hours = Math.max(1, Math.floor(remaining / (60 * 60 * 1000)));
    return { text: `Caduca en ${hours} ${hours === 1 ? 'hora' : 'horas'}`, urgent: true };
}

const TokenCard = ({ tokenData, formatDate, onEdit, onDelete, onCopy, onExtend }) => {
    const [showQr, setShowQr] = useState(false);
    const qrRef = useRef(null);
    const expired       = new Date() > new Date(tokenData.expires_at);
    const usageExceeded = tokenData.max_usage !== 0 && tokenData.usage_count >= tokenData.max_usage;
    const isActive      = !expired && !usageExceeded;
    const expiry        = expiryNotice(tokenData.expires_at);
    const usageRatio    = tokenData.max_usage > 0
        ? Math.min(100, Math.round((tokenData.usage_count / tokenData.max_usage) * 100))
        : null;
    const publicUrl     = `${window.location.origin}/${tokenData.url_token}`;

    /**
     * Descarga el QR como SVG.
     *
     * Se serializa el nodo que ya está en pantalla en vez de regenerarlo: así
     * el fichero es exactamente lo que se ha visto, y al ser vectorial sirve
     * igual para una pegatina que para un cartel.
     */
    // Escape para cerrar: el modal se abre desde un menú que ya se cierra así,
    // y sin esto la única salida es apuntar al botón.
    useEffect(() => {
        if (!showQr) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setShowQr(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showQr]);

    const downloadQr = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;

        const source = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const name = (tokenData.model_3d?.name || 'enlace').replace(/[^\w-]+/g, '_');
        link.download = `qr-${name}.svg`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const statusBadge = isActive
        ? { cls: 'badge-success', icon: <CheckCircle size={12} />, label: 'Vigente' }
        : expired
            ? { cls: 'badge-error',   icon: <XCircle size={12} />,      label: 'Expirado' }
            : { cls: 'badge-warning', icon: <Clock   size={12} />,      label: 'Uso máximo' };

    return (
        <div className={classNames(
            'card bg-base-200 border-l-4 shadow-md relative',
            isActive ? 'border-success' : expired ? 'border-error' : 'border-warning'
        )}>
            {/* Actions dropdown */}
            <div className="dropdown dropdown-end absolute top-2 right-2 z-10">
                <label tabIndex={0} className="btn btn-ghost btn-circle btn-sm">
                    <Settings size={16} />
                </label>
                <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-48 z-50 text-sm">
                    <li>
                        <button onClick={() => onEdit(tokenData)} className="flex items-center gap-2">
                            <PencilIcon className="w-4 h-4" /> Editar
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onCopy(tokenData)} className="flex items-center gap-2">
                            <ClipboardIcon className="w-4 h-4" /> Copiar enlace
                        </button>
                    </li>
                    <li>
                        <button onClick={() => setShowQr(true)} className="flex items-center gap-2">
                            <QrCode className="w-4 h-4" /> Ver QR
                        </button>
                    </li>
                    {/* Los dos plazos habituales. Para una fecha concreta está
                        "Editar"; esto es para no abrir el formulario cuando lo
                        único que hace falta es dar unos días más. */}
                    <li>
                        <button onClick={() => onExtend?.(tokenData, 7)} className="flex items-center gap-2">
                            <CalendarPlus className="w-4 h-4" /> Extender 7 días
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onExtend?.(tokenData, 30)} className="flex items-center gap-2">
                            <CalendarPlus className="w-4 h-4" /> Extender 30 días
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onDelete(tokenData)} className="flex items-center gap-2 text-error">
                            <TrashIcon className="w-4 h-4" /> Borrar
                        </button>
                    </li>
                </ul>
            </div>

            <div className="card-body p-4 gap-2 pr-10">
                {/* Model name + status */}
                <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-base-content text-sm leading-tight break-all">
                        {tokenData.model_3d?.name || 'Sin modelo'}
                    </p>
                    <span className={`badge badge-sm gap-1 shrink-0 ${statusBadge.cls}`}>
                        {statusBadge.icon} {statusBadge.label}
                    </span>
                </div>

                <div className="divider my-0" />

                {/* Details */}
                <div className="flex flex-col gap-1 text-xs text-base-content/70">
                    <div className="tooltip tooltip-top text-left" data-tip={tokenData.token}>
                        <span className="font-mono cursor-help">
                            Token: {tokenData.token?.substring(0, 10)}…
                        </span>
                    </div>
                    {/* Fecha absoluta + cuánto queda: la primera para
                        planificar, la segunda para decidir de un vistazo. */}
                    <span title={formatDate(tokenData.expires_at)}>
                        Expira: {formatDate(tokenData.expires_at)}
                    </span>
                    {expiry && !expired && (
                        <span className={classNames(
                            'flex items-center gap-1 font-medium',
                            expiry.urgent ? 'text-warning' : 'text-base-content/70'
                        )}>
                            {expiry.urgent && <AlertTriangle size={11} className="shrink-0" />}
                            {expiry.text}
                        </span>
                    )}
                    <span>
                        Usos: <strong className="text-base-content">{tokenData.usage_count}</strong>
                        {' '}/ {tokenData.max_usage === 0 ? 'Ilimitado' : tokenData.max_usage}
                    </span>
                    {/* Con cupo, el número solo no dice si queda mucho o poco. */}
                    {usageRatio !== null && (
                        <progress
                            className={classNames(
                                'progress h-1.5 w-full',
                                usageRatio >= 100 ? 'progress-error'
                                    : usageRatio >= 80 ? 'progress-warning'
                                        : 'progress-success'
                            )}
                            value={usageRatio}
                            max="100"
                            aria-label={`${tokenData.usage_count} de ${tokenData.max_usage} usos`}
                        />
                    )}
                </div>

                <div className="flex flex-wrap gap-1 mt-1">
                    <button onClick={() => onCopy(tokenData)} className="btn btn-xs btn-outline gap-1">
                        <Copy size={12} /> Copiar URL
                    </button>
                    {/* Comprobar que el enlace funciona era el motivo real de
                        copiarlo: se abre directamente, sin pasar por el portapapeles. */}
                    <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={classNames('btn btn-xs gap-1', isActive ? 'btn-outline' : 'btn-ghost')}
                        title={isActive ? 'Abrir la vista pública' : 'El enlace ya no es válido; se abrirá con error'}
                    >
                        <ExternalLink size={12} /> Abrir
                    </a>
                    <button
                        onClick={() => setShowQr(true)}
                        className="btn btn-xs btn-outline gap-1"
                        title="Mostrar el código QR del enlace"
                    >
                        <QrCode size={12} /> QR
                    </button>
                </div>
            </div>

            {/* En un portal a <body> a propósito: la tarjeta vive dentro de un
                contenedor con `backdrop-filter` (glassCard), y eso convierte a
                ese contenedor en el bloque contenedor de los `position: fixed`
                que haya dentro. El modal se anclaba entonces al panel con
                scroll en vez de al viewport, y salía arriba del todo. */}
            {showQr && createPortal(
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Código QR de ${tokenData.model_3d?.name || 'el enlace'}`}
                    onClick={() => setShowQr(false)}
                >
                    <div
                        className="bg-base-100 rounded-xl p-5 flex flex-col items-center gap-3 max-w-xs w-full my-auto shadow-2xl"
                        onClick={event => event.stopPropagation()}
                    >
                        <p className="font-semibold text-sm text-center">
                            {tokenData.model_3d?.name || 'Enlace público'}
                        </p>
                        {/* Fondo blanco fijo: un QR sobre fondo oscuro no lo lee
                            la mitad de los lectores, y este se imprime. */}
                        <div ref={qrRef} className="bg-white p-3 rounded-lg">
                            <QRCodeSVG value={publicUrl} size={192} level="M" />
                        </div>
                        <p className="text-[11px] text-base-content/60 break-all text-center">{publicUrl}</p>
                        {!isActive && (
                            <p className="text-xs text-warning text-center">
                                Este enlace ya no es válido: el QR llevará a un error.
                            </p>
                        )}
                        <div className="flex gap-2 w-full">
                            <button onClick={downloadQr} className="btn btn-sm btn-primary flex-1 gap-1">
                                <Download size={14} /> Descargar
                            </button>
                            <button onClick={() => setShowQr(false)} className="btn btn-sm btn-ghost">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default TokenCard;
